import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { headingId, parseBlocks } from '@/modules/docs/markdown-parse';

/**
 * The documentation's Markdown, rendered to React.
 *
 * ## Only what the pages use, and never HTML
 *
 * Headings, paragraphs, bullet and numbered lists, fenced code, bold, inline code and links. Every
 * piece of text becomes a text node, so nothing in a page can inject markup or script, and there is
 * no sanitiser to get wrong. A general-purpose Markdown library would parse far more than these
 * pages contain, and bring HTML passthrough with it.
 *
 * ## Links follow the reader
 *
 * Pages link to each other as `/docs/...`, the public addresses. Inside the dashboard the same page
 * is at `/help/...`, so `linkFor` rewrites each internal link to wherever the reader is. External
 * links open in a new tab.
 */

/** Bold, inline code and links, in the order they appear. Everything else is text. */
function inline(text: string, linkFor: (href: string) => string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let match: RegExpExecArray | null;
  let count = 0;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }

    const token = match[0];
    const key = `${keyPrefix}-${count++}`;

    if (token.startsWith('`')) {
      nodes.push(
        <code key={key} className="bg-muted rounded px-1.5 py-0.5 font-mono text-[0.85em] break-words">
          {token.slice(1, -1)}
        </code>,
      );
    } else if (token.startsWith('**')) {
      nodes.push(
        <strong key={key} className="text-foreground font-semibold">
          {inline(token.slice(2, -2), linkFor, key)}
        </strong>,
      );
    } else {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
      const label = link?.[1] ?? token;
      const href = link?.[2] ?? '#';

      if (/^https?:\/\//.test(href)) {
        nodes.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
            {label}
          </a>,
        );
      } else if (href.startsWith('#')) {
        nodes.push(
          <a key={key} href={href} className="text-primary hover:underline">
            {label}
          </a>,
        );
      } else {
        nodes.push(
          <Link key={key} to={linkFor(href)} className="text-primary hover:underline">
            {label}
          </Link>,
        );
      }
    }

    last = match.index + token.length;
  }

  if (last < text.length) {
    nodes.push(text.slice(last));
  }

  return nodes;
}

export function Markdown({
  source,
  linkFor = (href) => href,
  compact = false,
}: {
  source: string;
  /** Where an internal `/docs/...` link should go for this reader. */
  linkFor?: (href: string) => string;
  /** Smaller text and tighter spacing, for an answer in the Juni panel. */
  compact?: boolean;
}) {
  const blocks = parseBlocks(source);

  return (
    <div
      className={
        compact
          ? 'text-foreground space-y-2 text-sm leading-relaxed [&_h2]:pt-1 [&_h2]:text-base [&_h3]:pt-1 [&_h3]:text-sm'
          : 'text-muted-foreground space-y-4 text-[15px] leading-relaxed'
      }
    >
      {blocks.map((block, index) => {
        const key = `b${index}`;

        switch (block.type) {
          case 'heading':
            return block.level === 2 ? (
              <h2
                key={key}
                id={headingId(block.text)}
                className="text-foreground scroll-mt-24 pt-6 text-xl font-semibold tracking-tight first:pt-0"
              >
                {block.text}
              </h2>
            ) : (
              <h3 key={key} id={headingId(block.text)} className="text-foreground scroll-mt-24 pt-3 text-base font-semibold">
                {block.text}
              </h3>
            );

          case 'code':
            return (
              <pre
                key={key}
                className="bg-muted/60 border-border overflow-x-auto rounded-lg border px-4 py-3 font-mono text-[13px] leading-6 text-foreground"
              >
                <code>{block.text}</code>
              </pre>
            );

          case 'list': {
            const Tag = block.ordered ? 'ol' : 'ul';

            return (
              <Tag key={key} className={block.ordered ? 'list-decimal space-y-2 pl-6' : 'list-disc space-y-2 pl-6'}>
                {block.items.map((item, itemIndex) => (
                  <li key={`${key}-${itemIndex}`} className="pl-1">
                    {inline(item, linkFor, `${key}-${itemIndex}`)}
                  </li>
                ))}
              </Tag>
            );
          }

          case 'paragraph':
          default:
            return <p key={key}>{inline(block.text, linkFor, key)}</p>;
        }
      })}
    </div>
  );
}
